'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { Feature4 } from '@/components/sections/feature4';
import {
  BubbleQuickReplies,
  BubbleTimeSlots,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import Testimonials from '@/components/sections/testimonials';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    eyebrow: 'Channels',
    title: 'WhatsApp that feels human',
    subtitleA: 'Voice notes transcribed. Images understood.',
    subtitleB: 'Replies in 2 seconds — in your customer’s language.',
    voiceNote: '🎙 Voice note · 0:14',
    slotsDay: 'Fri, May 17',
    b1: 'Heard you — let me move your order to Friday delivery.',
    confirmLabel: 'Confirming…',
    // Deliberately Spanish: the demo proves the "in your customer's
    // language" claim in the subtitle.
    b2: '¿Pueden enviármelo a otra dirección?',
    b3: 'Claro — ¿qué dirección uso?',
    optHome: 'Home',
    optWork: 'Work',
    optNew: 'Type a new one',
  },
  ar: {
    eyebrow: 'القنوات',
    title: 'WhatsApp يحس فيه العميل إنه يكلّم إنسان',
    subtitleA: 'يسمع الرسائل الصوتية، ويقرأ الصور.',
    subtitleB: 'ويرد بثانيتين — بلغة عميلك.',
    voiceNote: '🎙 رسالة صوتية · 0:14',
    slotsDay: 'الجمعة ١٧ مايو',
    b1: 'سمعتك — خلّني أنقل طلبك لتوصيل يوم الجمعة.',
    confirmLabel: 'جارٍ التأكيد…',
    // Same Spanish demo as EN, but wrapped in ⁦…⁩ (LRI/PDI) — inside an
    // RTL paragraph the bare Latin text renders with "¿" and "?" swapped
    // onto the wrong ends of each line.
    b2: '⁦¿Pueden enviármelo a otra dirección?⁩',
    b3: '⁦Claro — ¿qué dirección uso?⁩',
    optHome: 'البيت',
    optWork: 'العمل',
    optNew: 'اكتب عنوان جديد',
  },
} as const;

export default function WhatsAppPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.title}
        subtitle={
          <>
            {c.subtitleA}
            <br />
            {c.subtitleB}
          </>
        }
        videoSrc="/home/features/old-man-whatsapp.mp4"
        // English unchanged; on /ar fall through to SubpageHero's localized
        // default ("الوكيل الذكي") instead of the untranslated "Open Agent".
        bubbleAgentName={locale === 'ar' ? undefined : 'Open Agent'}
        bubbles={[
          {
            role: 'user',
            text: c.voiceNote,
          },
          {
            role: 'agent',
            text: c.b1,
            widget: (
              <BubbleTimeSlots
                day={c.slotsDay}
                slots={['09:00', '11:00', '13:00', '15:00', '17:00']}
                selected="11:00"
                confirmLabel={c.confirmLabel}
              />
            ),
          },
          { role: 'user', text: c.b2 },
          {
            role: 'agent',
            text: c.b3,
            widget: (
              <BubbleQuickReplies
                options={[c.optHome, c.optWork, c.optNew]}
                selected={c.optHome}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['WhatsApp AI Features']}
        subtitle={t.channels['Meet customers where they already are']}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['Hears Voice Notes, Types Back Instantly'],
            description:
              t.channels[
                'Customers speak. AI transcribes, understands, and replies in text. Multi-language support included.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-1.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Sees Screenshots & Photos'],
            description:
              t.channels[
                'Customer sends error screenshot? Product photo? Invoice? Agent 5 sees it, reads it, solves it.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-2.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Support on Their Favorite App'],
            description:
              t.channels[
                '2 billion people use WhatsApp daily. Be available where your customers already spend their time.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Proactive Outreach That Converts'],
            description:
              t.channels[
                'Send order updates, abandoned cart reminders, and personalized offers. 98% open rates on WhatsApp.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-4.svg',
            imagePosition: 'left',
          },
          {
            title: t.channels['Never Lose Conversation Context'],
            description:
              t.channels[
                'Full message history saved forever. Customer returns weeks later? AI remembers everything.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-5.svg',
            imagePosition: 'right',
          },
          {
            title: t.channels['Human Takeovers Without Disruption'],
            description:
              t.channels[
                'Agent jumps in mid-conversation? Customer stays in same chat. Zero friction, total continuity.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-6.svg',
            imagePosition: 'left',
          },
        ]}
      />

      <Feature4
        heading={
          t.channels[
            'WhatsApp AI that connects to your existing support workflow seamlessly'
          ]
        }
      />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels['Find answers to common questions about WhatsApp support.']
        }
        items={[
          {
            question: t.channels['Can Agent 5 understand voice notes?'],
            answer:
              t.channels[
                'Yes. Agent 5 transcribes and understands voice notes in multiple languages, then responds appropriately.'
              ],
          },
          {
            question: t.channels['Can it see images customers send?'],
            answer:
              t.channels[
                'Yes. Agent 5 analyzes images, reads text from screenshots, and understands visual context to provide accurate help.'
              ],
          },
          {
            question: t.channels['Does it show typing indicators?'],
            answer:
              t.channels[
                'Yes. Customers see "typing..." while Agent 5 prepares responses, making the conversation feel natural.'
              ],
          },
          {
            question: t.channels['Can human agents see old WhatsApp messages?'],
            answer:
              t.channels[
                'Yes. Inbox WhatsApp mode shows agents the complete message history, not just recent messages. Full context from all past conversations.'
              ],
          },
          {
            question: t.channels['Can I send outbound WhatsApp messages?'],
            answer:
              t.channels[
                'Yes. Send proactive messages, notifications, and updates to customers. Automate outreach and broadcast to segments.'
              ],
          },
          {
            question:
              t.channels['How do WhatsApp handoffs to human agents work?'],
            answer:
              t.channels[
                'The same WhatsApp thread continues. Human agents see full history and reply directly. Customer stays in the same conversation.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
